/**
 * Drag-and-Drop Hook for Skills (group-aware)
 * Handles reordering within rows and cross-row drag
 */

import { useState } from "react";
import {
  DragEndEvent,
  DragStartEvent,
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { Skill } from "@/types/skills";

interface UseSkillDragDropOptions {
  onReorder: (
    skillOrders: Array<{
      id: string;
      display_order: number;
      display_row: number;
    }>,
  ) => void;
}

/**
 * Custom collision detection: prefer sortable items (closestCenter),
 * fall back to droppable containers (rectIntersection) for empty rows.
 */
const multiContainerCollision: CollisionDetection = (args) => {
  // First try closestCenter for sortable items
  const closestCenterCollisions = closestCenter(args);
  if (closestCenterCollisions.length > 0) {
    return closestCenterCollisions;
  }

  // Fall back to pointer-within for droppable containers (empty rows)
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  // Last resort: rect intersection
  return rectIntersection(args);
};

export function useSkillDragDrop({ onReorder }: UseSkillDragDropOptions) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };

  const handleDragEnd = (
    event: DragEndEvent,
    row0Skills: Skill[],
    row1Skills: Skill[],
  ) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);

    if (!over || active.id === over.id) return;

    const activeSkillId = active.id as string;
    const overSkillId = over.id as string;

    // Determine which rows the active item belongs to
    const activeInRow0 = row0Skills.some((s) => s.id === activeSkillId);

    // Check if dropped on a row container (droppable)
    const overIsRowContainer =
      overSkillId === "row-0-drop" || overSkillId === "row-1-drop";

    let newRow0 = [...row0Skills];
    let newRow1 = [...row1Skills];

    if (overIsRowContainer) {
      // Dropped on row container — move to that row's end
      const targetRow = overSkillId === "row-0-drop" ? 0 : 1;
      const sourceRow = activeInRow0 ? 0 : 1;
      if (sourceRow === targetRow) return;

      const sourceList = sourceRow === 0 ? newRow0 : newRow1;
      const targetList = targetRow === 0 ? newRow0 : newRow1;
      const idx = sourceList.findIndex((s) => s.id === activeSkillId);
      if (idx === -1) return;

      const [moved] = sourceList.splice(idx, 1);
      targetList.push(moved);

      if (sourceRow === 0) {
        newRow0 = sourceList;
        newRow1 = targetList;
      } else {
        newRow1 = sourceList;
        newRow0 = targetList;
      }
    } else {
      // Dropped on a skill item
      const overInRow0 = row0Skills.some((s) => s.id === overSkillId);

      if (activeInRow0 === overInRow0) {
        // Same row — reorder within
        if (activeInRow0) {
          const oldIdx = newRow0.findIndex((s) => s.id === activeSkillId);
          const newIdx = newRow0.findIndex((s) => s.id === overSkillId);
          newRow0 = arrayMove(newRow0, oldIdx, newIdx);
        } else {
          const oldIdx = newRow1.findIndex((s) => s.id === activeSkillId);
          const newIdx = newRow1.findIndex((s) => s.id === overSkillId);
          newRow1 = arrayMove(newRow1, oldIdx, newIdx);
        }
      } else {
        // Cross-row drag — move to other row at the drop position
        const sourceList = activeInRow0 ? newRow0 : newRow1;
        const targetList = activeInRow0 ? newRow1 : newRow0;
        const sourceIdx = sourceList.findIndex((s) => s.id === activeSkillId);
        const targetIdx = targetList.findIndex((s) => s.id === overSkillId);
        if (sourceIdx === -1 || targetIdx === -1) return;

        const [moved] = sourceList.splice(sourceIdx, 1);
        targetList.splice(targetIdx, 0, moved);

        if (activeInRow0) {
          newRow0 = sourceList;
          newRow1 = targetList;
        } else {
          newRow1 = sourceList;
          newRow0 = targetList;
        }
      }
    }

    // Build skill_orders array with both display_order and display_row
    const skillOrders = [
      ...newRow0.map((s, i) => ({
        id: s.id,
        display_order: i,
        display_row: 0,
      })),
      ...newRow1.map((s, i) => ({
        id: s.id,
        display_order: i,
        display_row: 1,
      })),
    ];
    onReorder(skillOrders);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  return {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection: multiContainerCollision,
  };
}
